import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Car, ParkingSquare, LogIn, LogOut, Wallet, Timer, CircleDollarSign, AlertTriangle, Hand, WifiOff, ShieldX, ClipboardX, TrendingUp, Activity } from "lucide-react";
import { Bar, BarChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis, Legend } from "recharts";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Kpi, Panel, PageHeader, StatusBadge, Spinner, Dot, DataTable } from "@/components/kit";
import { useApi, peso, dur, dt, label } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const COLORS = ["#F59E0B", "#3B82F6", "#10B981", "#06B6D4", "#EF4444"];
const tip = { contentStyle: { background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 } };

function Ops() {
  const { branch, can } = useAuth();
  const { data, reload } = useApi("/dashboard/branch", { branch_id: branch });
  useEffect(() => {
    const t = setInterval(reload, 15000);
    return () => clearInterval(t);
  }, [reload]);
  if (!data) return <Spinner />;
  const s = data.stats;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-6 stagger">
        <Kpi testid="kpi-inside" label="Vehicles inside" value={s.vehicles_inside} icon={Car} tone="text-cyan-500" />
        <Kpi testid="kpi-available" label="Slots available" value={s.slots_available} sub={`of ${s.slots_total}`} icon={ParkingSquare} tone="text-emerald-500" />
        <Kpi testid="kpi-occupancy" label="Occupancy" value={`${s.occupancy_pct}%`} icon={Activity} alert={s.occupancy_pct >= 90} />
        <Kpi testid="kpi-entries" label="Entries today" value={s.entries_today} icon={LogIn} />
        <Kpi testid="kpi-exits" label="Exits today" value={s.exits_today} icon={LogOut} />
        {can("view_financial") && <Kpi testid="kpi-revenue" label="Revenue today" value={peso(s.revenue_today)} icon={CircleDollarSign} tone="text-amber-500" />}
        <Kpi testid="kpi-avg" label="Avg duration" value={dur(s.avg_duration_min)} icon={Timer} />
        <Kpi testid="kpi-unpaid" label="Unpaid sessions" value={s.unpaid_sessions} icon={Wallet} />
        <Kpi testid="kpi-exceptions" label="Open exceptions" value={s.open_exceptions} icon={AlertTriangle} alert={s.open_exceptions > 0} />
        <Kpi testid="kpi-overrides" label="Overrides today" value={s.overrides_today} sub={`${s.pending_overrides} awaiting approval`} icon={Hand} alert={s.pending_overrides > 0} />
        <Kpi testid="kpi-offline" label="Offline devices" value={s.offline_devices} icon={WifiOff} alert={s.offline_devices > 0} />
        <Kpi testid="kpi-barrier-faults" label="Barrier faults" value={s.barrier_faults} icon={ShieldX} alert={s.barrier_faults > 0} />
        <Kpi testid="kpi-overdue" label="Overdue checks" value={s.overdue_checks} icon={ClipboardX} alert={s.overdue_checks > 0} />
      </div>
      <div className="grid gap-6 xl:grid-cols-3">
        <Panel title="Lanes & barriers (live)" className="xl:col-span-1" testid="lanes-panel" action={<Link to="/barriers" className="text-xs text-primary">Manage →</Link>}>
          <div className="space-y-2">
            {data.lanes.map((l) => (
              <div key={l.id} className="flex items-center justify-between rounded-md border border-border/50 px-3 py-2" data-testid={`lane-${l.id}`}>
                <div className="flex items-center gap-2.5">
                  <Dot ok={l.barrier?.status === "online"} />
                  <div>
                    <div className="text-sm font-semibold">{l.name} <span className="text-xs text-muted-foreground">({label(l.direction)})</span></div>
                    <div className="font-mono text-[11px] text-muted-foreground">{l.barrier?.device_code} • {l.barrier?.last_command_response || "idle"}</div>
                  </div>
                </div>
                <StatusBadge status={l.barrier?.status === "online" ? l.barrier?.barrier_state : l.barrier?.status} />
              </div>
            ))}
          </div>
        </Panel>
        <Panel title="Hourly traffic today" className="xl:col-span-2">
          <div className="h-64">
            <ResponsiveContainer>
              <BarChart data={data.hourly}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="hour" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis allowDecimals={false} tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                <Tooltip {...tip} />
                <Legend />
                <Bar dataKey="entries" fill="#06B6D4" radius={[3, 3, 0, 0]} />
                <Bar dataKey="exits" fill="#F59E0B" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>
      </div>
      <div className="grid gap-6 xl:grid-cols-2">
        <Panel title="Zone occupancy">
          <div className="space-y-3">
            {data.zones.map((z) => {
              const p = z.total ? Math.round((z.occupied * 100) / z.total) : 0;
              return (
                <div key={z.id}>
                  <div className="mb-1 flex justify-between text-xs"><span className="font-medium">{z.name} <span className="text-muted-foreground">({z.floor})</span></span><span className="font-mono">{z.occupied}/{z.total} • {p}%</span></div>
                  <div className="h-2 rounded-full bg-muted"><div className={`h-2 rounded-full ${p >= 90 ? "bg-red-500" : p >= 70 ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${p}%` }} /></div>
                </div>
              );
            })}
          </div>
        </Panel>
        <Panel title="Recent vehicle events">
          <div className="space-y-1.5">
            {data.recent_events.map((e) => (
              <div key={e.id} className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2"><StatusBadge status={e.allowed ? e.type : "failed"} tone={e.allowed ? (e.type === "entry" ? "cyan" : "green") : "red"} /><span className="plate text-xs">{e.plate || "—"}</span></span>
                <span className="hidden text-xs text-muted-foreground sm:inline">{e.lane_name}{e.reason ? ` • ${e.reason}` : ""}</span>
                <span className="font-mono text-xs text-muted-foreground">{dt(e.at)}</span>
              </div>
            ))}
          </div>
        </Panel>
      </div>
    </div>
  );
}

function HeadOffice() {
  const { data } = useApi("/dashboard/headoffice", {});
  if (!data) return <Spinner />;
  const t = data.totals;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-6 stagger">
        <Kpi testid="ho-revenue-today" label="Revenue today" value={peso(t.revenue_today)} icon={CircleDollarSign} tone="text-amber-500" />
        <Kpi testid="ho-revenue-month" label="Revenue this month" value={peso(t.revenue_month)} icon={TrendingUp} tone="text-amber-500" />
        <Kpi testid="ho-vehicles-today" label="Vehicles today" value={t.entries_today} icon={Car} />
        <Kpi testid="ho-occupancy" label="Current occupancy" value={`${t.occupancy_pct}%`} icon={Activity} />
        <Kpi testid="ho-active" label="Active sessions" value={t.vehicles_inside} icon={ParkingSquare} tone="text-cyan-500" />
        <Kpi testid="ho-unpaid" label="Unpaid sessions" value={t.unpaid_sessions} icon={Wallet} />
        <Kpi testid="ho-barrier-faults" label="Barrier faults" value={t.barrier_faults} icon={ShieldX} alert={t.barrier_faults > 0} />
        <Kpi testid="ho-downtime" label="Device downtime" value={`${t.device_downtime_min}m`} icon={WifiOff} alert={t.device_downtime_min > 0} />
        <Kpi testid="ho-overrides" label="Manual overrides" value={t.overrides_today} icon={Hand} />
        <Kpi testid="ho-exceptions" label="Open exceptions" value={t.open_exceptions} icon={AlertTriangle} alert={t.open_exceptions > 0} />
        <Kpi testid="ho-overdue" label="Overdue checks" value={t.overdue_checks} icon={ClipboardX} alert={t.overdue_checks > 0} />
      </div>
      <Panel title="Revenue trend — last 14 days (₱)">
        <div className="h-72">
          <ResponsiveContainer>
            <LineChart data={data.trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
              <YAxis tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
              <Tooltip {...tip} formatter={(v) => peso(v)} />
              <Legend />
              {data.codes.map((c, i) => <Line key={c} dataKey={c} stroke={COLORS[i % 5]} strokeWidth={2} dot={false} />)}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Panel>
      <DataTable testid="ho-branch-table" rows={data.branches} columns={[
        { key: "code", label: "Branch", render: (r) => <span className="font-semibold">{r.code} <span className="font-normal text-muted-foreground">{r.name}</span></span> },
        { key: "revenue_today", label: "Rev. today", render: (r) => <span className="font-mono">{peso(r.revenue_today)}</span> },
        { key: "revenue_month", label: "Rev. month", render: (r) => <span className="font-mono">{peso(r.revenue_month)}</span> },
        { key: "entries_today", label: "Entries" },
        { key: "vehicles_inside", label: "Inside" },
        { key: "occupancy_pct", label: "Occ.", render: (r) => `${r.occupancy_pct}%` },
        { key: "unpaid_sessions", label: "Unpaid" },
        { key: "barrier_faults", label: "Barrier faults", render: (r) => <span className={r.barrier_faults ? "font-bold text-red-500" : ""}>{r.barrier_faults}</span> },
        { key: "device_downtime_min", label: "Downtime", render: (r) => `${r.device_downtime_min}m` },
        { key: "overrides_today", label: "Overrides" },
        { key: "open_exceptions", label: "Exceptions" },
        { key: "overdue_checks", label: "Overdue" },
      ]} />
    </div>
  );
}

export default function Dashboard() {
  const { can, user } = useAuth();
  const ho = can("view_financial", "view_technical") && user.all_branches;
  return (
    <div className="fade-up">
      <PageHeader title="Operations Dashboard" subtitle={`Live • Asia/Manila • ${new Date().toLocaleDateString("en-PH", { timeZone: "Asia/Manila", dateStyle: "full" })}`} testid="dashboard-header" />
      <Tabs defaultValue="ops">
        <TabsList>
          <TabsTrigger value="ops" data-testid="tab-ops">Branch Operations</TabsTrigger>
          {ho && <TabsTrigger value="ho" data-testid="tab-headoffice">Head Office (All Branches)</TabsTrigger>}
        </TabsList>
        <TabsContent value="ops" className="mt-5"><Ops /></TabsContent>
        {ho && <TabsContent value="ho" className="mt-5"><HeadOffice /></TabsContent>}
      </Tabs>
    </div>
  );
}
